import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Container, Row, Col, Form, InputGroup, Alert, ListGroup } from 'react-bootstrap';
import { FiCalendar, FiUsers, FiDollarSign, FiCheckCircle, FiClock, FiShield, FiSearch, FiMapPin } from 'react-icons/fi';
import { publicApi } from '../services/api';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    // Allow empty search to show all businesses
    // if (!searchQuery.trim() && !searchCity.trim()) {
    //   setSearchError('Please enter a business name or city to search');
    //   return;
    // }

    setSearching(true);
    setSearchError('');

    try {
      const params = {};
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (searchCity.trim()) params.city = searchCity.trim();

      console.log('Searching with params:', params);
      console.log('API base URL:', process.env.NODE_ENV === 'production' ? 'relative' : (process.env.REACT_APP_API_URL || 'http://localhost:3001'));
      console.log('Full URL would be:', process.env.NODE_ENV === 'production' ? '/public/businesses/search' : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/public/businesses/search`);
      
      const response = await publicApi.searchBusinesses(params);
      console.log('Search response:', response);
      
      if (response.data && response.data.success) {
        const businesses = response.data.data?.businesses || [];
        console.log(`Found ${businesses.length} businesses`);
        setSearchResults(businesses);
        if (businesses.length === 0) {
          setSearchError('No businesses found. Try a different search term.');
        }
      } else {
        setSearchError(response.data?.message || 'Failed to search businesses');
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to search businesses. Please try again.';
      setSearchError(errorMessage);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <div className="hero-section">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <h1 className="fade-in">Better Business Booking</h1>
              <p className="lead fade-in" style={{ animationDelay: '0.2s' }}>
                Streamline your appointments, manage your schedule, and grow your business
              </p>
              <div className="d-grid gap-3 d-md-flex justify-content-center mt-4 fade-in" style={{ animationDelay: '0.4s' }}>
                <Button 
                  variant="light" 
                  size="lg" 
                  className="px-5 py-3" 
                  style={{ fontWeight: 600 }}
                  onClick={() => navigate('/signup')}
                >
                  Get Started Free
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  className="px-5 py-3" 
                  style={{ fontWeight: 600 }}
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        {/* Customer Search & Booking Section */}
        <Row className="mt-5 mb-5">
          <Col lg={10} className="mx-auto">
            <Card className="shadow-custom border-0">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <FiCalendar style={{ fontSize: '3rem', color: 'var(--primary-color)', marginBottom: '1rem' }} />
                  <h2 className="text-gradient mb-3">Find & Book Appointments</h2>
                  <p className="text-muted">
                    Search for businesses and schedule appointments instantly
                  </p>
                </div>

                <Form onSubmit={handleSearch}>
                  <Row className="g-3">
                    <Col md={5}>
                      <InputGroup>
                        <InputGroup.Text>
                          <FiSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search by business name..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <InputGroup>
                        <InputGroup.Text>
                          <FiMapPin />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="City (optional)"
                          value={searchCity}
                          onChange={(e) => setSearchCity(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Button 
                        variant="primary" 
                        type="submit" 
                        className="w-100"
                        disabled={searching}
                      >
                        {searching ? 'Searching...' : 'Search'}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {searchError && (
                  <Alert variant="danger" className="mt-3">
                    {searchError}
                  </Alert>
                )}

                {searchResults.length > 0 && (
                  <div className="mt-4">
                    <h5 className="mb-3">Search Results ({searchResults.length})</h5>
                    <ListGroup>
                      {searchResults.map(business => (
                        <ListGroup.Item key={business.id} className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              {business.branding_logo && (
                                <img 
                                  src={business.branding_logo} 
                                  alt={business.name}
                                  style={{ width: '40px', height: '40px', borderRadius: '4px', marginRight: '12px', objectFit: 'cover' }}
                                />
                              )}
                              <div>
                                <h6 className="mb-1">{business.name}</h6>
                                {business.location_city && business.location_state && (
                                  <small className="text-muted">
                                    <FiMapPin className="me-1" />
                                    {business.location_city}, {business.location_state}
                                  </small>
                                )}
                              </div>
                            </div>
                            {business.description && (
                              <p className="text-muted mb-2 small">{business.description}</p>
                            )}
                            {business.contactInfo_phone && (
                              <small className="text-muted d-block">📞 {business.contactInfo_phone}</small>
                            )}
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/book/${business.id}`)}
                            className="ms-3"
                          >
                            Book Now
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {searchResults.length === 0 && !searching && searchQuery && !searchError && (
                  <Alert variant="info" className="mt-3">
                    No businesses found. Try a different search term.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Features Section */}
        <Row className="mt-5 mb-5">
          <Col md={4} className="mb-4">
            <Card className="h-100 feature-card shadow-custom">
              <Card.Body>
                <div className="feature-icon">
                  <FiCalendar />
                </div>
                <Card.Title>Easy Scheduling</Card.Title>
                <Card.Text style={{ color: 'var(--gray-600)' }}>
                  Manage your appointments with our intuitive calendar interface. 
                  Set availability, view bookings, and never miss an appointment.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-4">
            <Card className="h-100 feature-card shadow-custom">
              <Card.Body>
                <div className="feature-icon">
                  <FiUsers />
                </div>
                <Card.Title>Customer Management</Card.Title>
                <Card.Text style={{ color: 'var(--gray-600)' }}>
                  Keep track of your customers and their booking history. 
                  Send automated reminders and notifications to keep everyone informed.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-4">
            <Card className="h-100 feature-card shadow-custom">
              <Card.Body>
                <div className="feature-icon">
                  <FiDollarSign />
                </div>
                <Card.Title>Affordable Pricing</Card.Title>
                <Card.Text style={{ color: 'var(--gray-600)' }}>
                  No hidden fees, no complex pricing tiers. 
                  Simple, transparent pricing that grows with your business.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Benefits Section */}
        <Row className="mt-5 mb-5">
          <Col lg={6} className="mb-4">
            <Card className="shadow-custom">
              <Card.Body className="p-4">
                <h3 className="text-gradient mb-3">For Businesses</h3>
                <ul className="list-unstyled">
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Complete Dashboard</strong>
                      <p className="text-muted mb-0">Manage services, appointments, and availability all in one place</p>
                    </div>
                  </li>
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Automated Notifications</strong>
                      <p className="text-muted mb-0">Send confirmation and reminder emails automatically</p>
                    </div>
                  </li>
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Real-time Updates</strong>
                      <p className="text-muted mb-0">See new bookings and changes as they happen</p>
                    </div>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="mb-4">
            <Card className="shadow-custom">
              <Card.Body className="p-4">
                <h3 className="text-gradient mb-3">For Customers</h3>
                <ul className="list-unstyled">
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Quick Booking</strong>
                      <p className="text-muted mb-0">Book appointments in under 30 seconds, no account required</p>
                    </div>
                  </li>
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Instant Confirmation</strong>
                      <p className="text-muted mb-0">Receive immediate confirmation via email</p>
                    </div>
                  </li>
                  <li className="mb-3 d-flex align-items-start">
                    <FiCheckCircle className="me-2 mt-1" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }} />
                    <div>
                      <strong>Easy Rescheduling</strong>
                      <p className="text-muted mb-0">View and manage your appointments anytime</p>
                    </div>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* CTA Section */}
        <Row className="mt-5 mb-5">
          <Col className="text-center">
            <Card className="shadow-custom" style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)', color: 'white', border: 'none' }}>
              <Card.Body className="p-5">
                <h2 className="mb-3" style={{ color: 'white' }}>Ready to Get Started?</h2>
                <p className="lead mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Join thousands of businesses already using Triple B to manage their appointments
                </p>
                <Button 
                  variant="light" 
                  size="lg" 
                  className="px-5 py-3" 
                  style={{ fontWeight: 600 }}
                  onClick={() => navigate('/signup')}
                >
                  Create Your Free Account
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HomePage;
